import { Injectable, computed, signal } from '@angular/core';
import { ShellModulo } from '@intelishs-group/layout';
import type { EntradaDeMenuIrA } from '@intelishs-group/ui/ir-a';
import type { GrupoMigas } from '@intelishs-group/ui';

/**
 * Definicion de un modulo. Extiende el contrato del shell con lo que el shell
 * NO debe saber: quien tiene permiso de verlo.
 */
export interface ModuloNav extends ShellModulo {
  /**
   * Exige ACCESO TOTAL para verlo. `undefined` o `false` = lo ve toda sesion.
   *
   * ── Por que un hecho y no una lista de roles ────────────────────────────
   *
   * Aqui habia `rolesPermitidos: readonly string[]`, y las ocho entradas del
   * catalogo llevaban la MISMA: `['portal:superadmin']`, el literal que emitia
   * el token propio de este portal. Al pasar a la sesion de plataforma el rol
   * activo empezo a llamarse `superadministrador` y el filtro dejo de casar con
   * nada — el riel se quedo con `inicio`, que es la unica entrada sin filtro.
   * Sin error, sin ruta rota, sin nada en consola.
   *
   * Y fue la SEGUNDA vez. La primera la contaba el docblock de `ROL_PORTAL`
   * —en `schemas/roles.ts`, retirado junto con `AuthHttpService`—: alli el
   * literal decia `administrador`, que era el que emitia un proveedor SIMULADO,
   * y al entrar el real el riel tambien salio vacio. Mismo fallo, tercer
   * emisor. Un mecanismo que hay que mantener sincronizado a mano con quien
   * emite la sesion se rompe cada vez que el emisor cambia, y
   * **se rompe callado**, porque un filtro que no casa con nada no es un error:
   * es un menu vacio.
   *
   * `esSuperadmin` es un hecho de identidad (`es_superadmin`), no una
   * convencion de nombres, asi que no lo puede acuñar un rol de aplicacion.
   *
   * Sigue siendo una BANDERA y no un `true` implicito en todo el catalogo
   * porque el riel tiene sitio para los schemas que faltan (`sesion`,
   * `migracion`), y alguno puede no ser de acceso total.
   */
  readonly soloSuperadmin?: boolean;

  /**
   * Va al FINAL del riel, sin importar en que lugar del catalogo se declare.
   *
   * Lo lleva la bitacora: registra lo que se hace en todas las demas
   * pantallas, asi que se lee como el cierre del menu. Es una bandera y no el
   * orden del arreglo porque el orden se rompe solo: quien agregue un modulo al
   * final del catalogo —que es lo natural— lo dejaria debajo de la bitacora.
   */
  readonly alFinal?: boolean;

  /**
   * Id del GRUPO al que pertenece, o `undefined` si va suelto en el riel.
   *
   * Un modulo agrupado NO aparece en el riel: aparece como tarjeta dentro del
   * hub de su grupo, y quien se ve en el riel es el grupo. Ver `GRUPOS`.
   */
  readonly grupo?: string;
}

/**
 * Un grupo de modulos: una entrada del riel que lleva a un hub de tarjetas.
 *
 * La forma —selector de tarjetas + pantalla de detalle— la estreno la pantalla
 * de catalogos, retirada el 2026-10-02, y de ahi se generalizo a los conjuntos
 * que quedan.
 */
export interface GrupoNav {
  readonly id: string;
  readonly etiqueta: string;
  readonly icono: string;
  readonly ruta: string;
  readonly descripcion: string;
  /** Frase del encabezado del hub. Mas larga que la del tooltip del riel. */
  readonly resumen: string;
  readonly soloSuperadmin?: boolean;
}

/**
 * LOS GRUPOS. Cada uno es una entrada del riel y una pantalla de hub.
 *
 * ── Por que agrupar, y por que por SCHEMA ───────────────────────────────────
 *
 * El trabajo de este portal es administrar la base, asi que el modelo mental
 * del usuario y el de la base son el mismo. Agrupar por schema no es exponer la
 * plomeria: es nombrar las cosas como son.
 *
 * ── El costo, dicho en voz alta ─────────────────────────────────────────────
 *
 * Un hub sobre tres pantallas no DESAMBIGUA —para eso hace falta algo como los
 * quince hijos que tenia la pantalla de catalogos— asi que cobra un clic extra
 * en las tres pantallas mas usadas del portal. Se asumio a cambio de la consistencia: los
 * tres conjuntos se navegan igual, y el riel queda en cinco entradas con sitio
 * para los schemas que faltan (`sesion`, `migracion`).
 *
 * ── «Acceso» junta DOS schemas, y el nombre no miente ───────────────────────
 *
 * Aplicaciones y roles son `acceso`; cuentas es `identidad`. El grupo se llama
 * por su TEMA y no por un schema, precisamente para no afirmar una
 * correspondencia que no existe. «Recursos humanos» si es `rh` entero.
 */
export const GRUPOS: readonly GrupoNav[] = [
  {
    id: 'acceso',
    etiqueta: 'Acceso',
    icono: 'safety-certificate',
    ruta: '/acceso',
    descripcion: 'Quien entra, con que rol y a que aplicacion',
    resumen:
      'Los esquemas `acceso` e `identidad`: las aplicaciones del ecosistema, los roles que ' +
      'otorgan sus modulos, y las cuentas que los tienen.',
    soloSuperadmin: true,
  },
];

/**
 * EL CATALOGO DE MODULOS. Fuente unica de verdad de la navegacion.
 *
 * En esta aplicacion el menu NO viene de base de datos: es fijo y se declara
 * aqui. Esa decision es deliberada —unos pocos modulos que no cambian no
 * justifican una tabla, un endpoint y una peticion en cada arranque—, pero el
 * servicio se escribio con la forma que tendria si viniera del servidor: el
 * resto de la app consume `modulos()`, nunca esta constante. El dia que haya
 * que traerlo por HTTP cambia esta clase y nada mas.
 *
 * La ruta declarada aqui y la del `app.routes.ts` tienen que coincidir. No hay
 * forma barata de que el compilador lo garantice, asi que lo valida
 * `tools/audit-arquitectura.mjs` (R4): una ruta de menu que no existe se
 * manifiesta como un item que no navega, sin error en consola.
 *
 * Al agregar un modulo: crea su pagina en `src/app/pages/`, registra la ruta en
 * `app.routes.ts` con `sesionGuard`, y añade la entrada aqui. En ese orden.
 */
/**
 * EL ORDEN DEL RIEL, que no es alfabetico ni historico.
 *
 * Inicio, Acceso, Recursos humanos, Facturacion.
 *
 * Los CATALOGOS se retiraron el 2026-10-02 porque `catgeneral` vive en
 * `dbfact` y leerlo habria sido pedirselo a `catalogosapi`: convertir al portal
 * en cliente de otro servicio. Volvieron el 2026-10-20 por el otro camino, el
 * correcto: la API abre una conexion por base (`dbusers`, `dbfact`,
 * `dbservicio`) con el usuario `usradminportal`, y administra cada una
 * DIRECTAMENTE. El catalogo general (`/catalogos/general`) entra igual.
 *
 * Los grupos se insertan donde esta su PRIMER HIJO, asi que este orden es el
 * que se ve: mover un grupo es mover su primer modulo.
 */
const CATALOGO: readonly ModuloNav[] = [
  {
    id: 'inicio',
    etiqueta: 'Inicio',
    icono: 'home',
    ruta: '/inicio',
    descripcion: 'Portada del portal',
  },
  // El ORDEN de este bloque es el del riel Y el de las tarjetas del hub: las dos
  // salen de aqui. «Cuentas» va primero porque es a donde se entra a diario
  // —quien existe y si puede entrar—, mientras que aplicaciones y roles se
  // tocan al configurar, que pasa de vez en cuando.
  {
    id: 'cuentas',
    etiqueta: 'Cuentas',
    icono: 'user',
    ruta: '/acceso/cuentas',
    descripcion: 'Quien existe, que roles tiene y si puede entrar',
    soloSuperadmin: true,
    grupo: 'acceso',
  },
  {
    id: 'aplicaciones',
    etiqueta: 'Aplicaciones',
    icono: 'appstore',
    ruta: '/acceso/aplicaciones',
    descripcion: 'Aplicativos del ecosistema y sus modulos',
    soloSuperadmin: true,
    grupo: 'acceso',
  },
  {
    id: 'roles',
    etiqueta: 'Roles',
    icono: 'idcard',
    ruta: '/acceso/roles',
    descripcion: 'Roles de acceso y los modulos que otorgan',
    soloSuperadmin: true,
    grupo: 'acceso',
  },
  // SUELTA en el riel, sin `grupo`: la bitacora registra cambios de `acceso`,
  // `catalogo`, `identidad` y `rh`, asi que meterla en un grupo afirmaria que es
  // de ese tema. Siempre al final del riel (`alFinal`): registra lo que pasa en
  // todas las demas pantallas.
  {
    id: 'bitacora',
    etiqueta: 'Bitácora',
    icono: 'history',
    ruta: '/bitacora',
    descripcion: 'Que fila cambio, como estaba, como quedo, quien y por que',
    soloSuperadmin: true,
    alFinal: true,
  },
  // SUELTA en el riel, como la bitacora y por la misma razon: los catalogos
  // viven en tres bases y no son de ningun grupo. En el riel sale ANTES de la
  // bitacora aunque se declare despues: ella lleva `alFinal`.
  {
    id: 'catalogos',
    etiqueta: 'Catálogos',
    icono: 'database',
    ruta: '/catalogos',
    descripcion: 'Las listas que usan las aplicaciones, cada una en su base',
    soloSuperadmin: true,
  },
  //
  // OJO: no dejes aqui entradas de ejemplo COMENTADAS. R4 de la auditoria lee
  // este archivo con una expresion regular y no descarta comentarios, asi que
  // una `ruta:` comentada que no exista en `app.routes.ts` se reporta como ruta
  // fantasma.
];

@Injectable({ providedIn: 'root' })
export class NavegacionService {
  /**
   * Si la sesion activa tiene acceso total. `null` mientras no hay sesion.
   *
   * Lo escribe la capa de autenticacion desde `app.ts`. Sin sesion, `modulos()`
   * sale VACIO: enumerar modulos que la sesion no respalda filtra que existe en
   * el sistema.
   *
   * **Son TRES estados y no dos**, y se distinguen a proposito:
   *
   *     null    no hay sesion             → menu vacio
   *     false   sesion sin acceso total   → solo lo que no lo exige
   *     true    superadministrador        → el catalogo entero
   *
   * `null` y `false` acaban pareciendose hoy —el portal es del
   * superadministrador— pero confundirlos ocultaria que la sesion cargo y que
   * lo que falta es el privilegio, que son diagnosticos distintos.
   */
  private readonly superadmin = signal<boolean | null>(null);

  /**
   * Lo que el RIEL pinta: los modulos sueltos y los GRUPOS, no los hijos de un
   * grupo.
   *
   * Un modulo agrupado no aparece aqui — aparece como tarjeta en el hub de su
   * grupo, y el riel muestra el grupo. Si apareciera en los dos sitios el riel
   * volveria a tener ocho entradas y el hub no habria servido de nada.
   *
   * El orden es el del catalogo, con los grupos insertados donde esta su primer
   * hijo: asi «Acceso» queda donde estaba «Aplicaciones» y el riel no se
   * reordena de golpe para quien ya lo tenia aprendido.
   */
  readonly modulos = computed<readonly ShellModulo[]>(() => {
    const superadmin = this.superadmin();
    if (superadmin === null) return [];

    const salida: ShellModulo[] = [];
    const alFinal: ShellModulo[] = [];
    const gruposPuestos = new Set<string>();

    for (const m of CATALOGO) {
      if (!this.visible(m.soloSuperadmin, superadmin)) continue;

      if (m.alFinal) {
        alFinal.push(m);
        continue;
      }

      if (m.grupo === undefined) {
        salida.push(m);
        continue;
      }

      // Primer hijo visible del grupo: aqui va el grupo, una sola vez.
      if (gruposPuestos.has(m.grupo)) continue;
      const grupo = GRUPOS.find((g) => g.id === m.grupo);
      if (!grupo || !this.visible(grupo.soloSuperadmin, superadmin)) continue;

      gruposPuestos.add(m.grupo);
      salida.push({
        id: grupo.id,
        etiqueta: grupo.etiqueta,
        icono: grupo.icono,
        ruta: grupo.ruta,
        descripcion: grupo.descripcion,
      });
    }

    return [...salida, ...alFinal];
  });

  /**
   * Las pantallas para «Ir a…» (Ctrl+K): lo que pinta el riel y, debajo de cada
   * grupo, sus hijas con el grupo como `contexto` («Cuentas · Acceso»).
   *
   * Hace falta porque el shell arma sus Pantallas con `modulos()`, que trae el
   * GRUPO y no sus hijas: Ctrl+K no encontraba Cuentas, Roles ni Aplicaciones,
   * las tres más usadas. La fuente que lee esto lleva el `tipo` de la del shell y
   * la reemplaza en su lugar (`@intelishs-group/ui` ≥ 5.3.1).
   *
   * Sale de `modulos()` y `modulosDeGrupo()`, o sea del MISMO catálogo y con el
   * MISMO filtro de sesión que el riel y los hubs: una tercera lista sería la
   * desincronización que el docblock de `modulosDeGrupo` describe.
   */
  readonly pantallas = computed<readonly EntradaDeMenuIrA[]>(() =>
    this.modulos().flatMap((m) => {
      const grupo = GRUPOS.find((g) => g.id === m.id);
      if (!grupo) return [m];
      const hijas = this.modulosDeGrupo(grupo.id).map((h) => ({
        id: h.id,
        etiqueta: h.etiqueta,
        icono: h.icono,
        ruta: h.ruta,
        contexto: grupo.etiqueta,
      }));
      return [m, ...hijas];
    }),
  );

  /**
   * Los modulos de un grupo, filtrados por la sesion. Es lo que pinta su hub.
   *
   * Sale del MISMO catalogo que el riel a proposito: dos listas —una para el
   * menu y otra para las tarjetas— se desincronizan en cuanto alguien agregue
   * un modulo y toque solo una, y el sintoma seria una pantalla que existe pero
   * a la que no se llega, sin ningun error. Es la misma clase de defecto que R4
   * vigila entre el menu y el router.
   */
  modulosDeGrupo(grupoId: string): readonly ModuloNav[] {
    const superadmin = this.superadmin();
    if (superadmin === null) return [];
    return CATALOGO.filter(
      (m) => m.grupo === grupoId && this.visible(m.soloSuperadmin, superadmin),
    );
  }

  /** La definicion de un grupo, para el encabezado de su hub. */
  /**
   * El mismo booleano que filtra el menú, de solo lectura: `null` sin sesión.
   *
   * Lo lee el inicio para la sección de actividad, que no corresponde a ningún
   * módulo del riel —la bitácora y las sesiones cruzan todos los esquemas— y
   * cuyos endpoints piden superadministrador.
   */
  readonly accesoTotal = this.superadmin.asReadonly();

  /**
   * ¿La sesión puede ver este módulo, esté suelto en el riel o dentro de un hub?
   *
   * Lo usa el inicio para decidir qué secciones de métricas pinta. Aplica el
   * MISMO filtro que el riel y los hubs, así que una métrica nunca enseña datos
   * de una pantalla a la que la sesión no llega: sería filtrar qué existe, que
   * es justo lo que el menú vacío sin sesión evita.
   */
  puedeVer(moduloId: string): boolean {
    const superadmin = this.superadmin();
    if (superadmin === null) return false;
    const m = CATALOGO.find((x) => x.id === moduloId);
    if (!m || !this.visible(m.soloSuperadmin, superadmin)) return false;
    if (m.grupo === undefined) return true;
    const g = GRUPOS.find((x) => x.id === m.grupo);
    return !!g && this.visible(g.soloSuperadmin, superadmin);
  }

  grupo(grupoId: string): GrupoNav | undefined {
    return GRUPOS.find((g) => g.id === grupoId);
  }

  /**
   * Lo que no exige acceso total lo ve toda sesion; lo que si, solo quien lo
   * tiene. Ver `ModuloNav.soloSuperadmin` para por que es un hecho y no una
   * lista de roles.
   */
  private visible(soloSuperadmin: boolean | undefined, superadmin: boolean): boolean {
    return !soloSuperadmin || superadmin;
  }

  /**
   * El catalogo que leen las migas de `@intelishs-group/ui` (>= 5.3.8), que lo
   * registra `app.config.ts` con `proveerMigas()`: cada grupo con sus modulos.
   *
   * Sin filtrar por sesion, igual que la copia local (`app-migas`) que sustituye:
   * resolvia la pantalla sobre el catalogo COMPLETO. Sale del MISMO catalogo que
   * el riel y los hubs, asi que mover una pantalla de grupo mueve tambien su miga.
   */
  readonly gruposMigas: readonly GrupoMigas[] = GRUPOS.map((g) => ({
    etiqueta: g.etiqueta,
    icono: g.icono,
    ruta: g.ruta,
    pantallas: CATALOGO.filter((m) => m.grupo === g.id).map((m) => ({
      etiqueta: m.etiqueta,
      ruta: m.ruta,
    })),
  }));

  /** Catalogo completo, sin filtrar. Para pantallas de administracion. */
  readonly catalogo: readonly ModuloNav[] = CATALOGO;

  /**
   * Lo llama `app.ts` con lo que diga la sesion, y `null` cuando no hay.
   *
   * Recibe el HECHO ya resuelto, no la sesion: este servicio no conoce la
   * autenticacion a proposito —si la inyectara no se podria probar sin montar
   * media aplicacion— y la union ocurre en la raiz.
   */
  establecerSuperadmin(superadmin: boolean | null): void {
    this.superadmin.set(superadmin);
  }

  /**
   * Modulo al que pertenece una URL. Sirve para rotular la pantalla actual sin
   * repetir el nombre del modulo en cada pagina.
   */
  moduloDeRuta(url: string): ModuloNav | undefined {
    // La coincidencia MAS LARGA, no la primera: `/x/y` casa con `/x` y con
    // `/x/y`, y `find` devolveria el que este antes en el catalogo — o sea el
    // equivocado, segun el orden de declaracion.
    const limpia = url.split('?')[0].split('#')[0];
    let ganador: ModuloNav | undefined;
    // Recorre el catalogo COMPLETO, no `modulos()`: estando en
    // `/acceso/roles` hay que devolver «Roles» —que es lo que rotula la
    // pantalla— aunque el riel muestre «Acceso». La coincidencia mas larga es
    // justo lo que distingue los dos casos.
    for (const m of CATALOGO) {
      if (limpia !== m.ruta && !limpia.startsWith(`${m.ruta}/`)) continue;
      if (!ganador || m.ruta.length > ganador.ruta.length) ganador = m;
    }
    return ganador;
  }
}
